(() => {
  'use strict';

  const VERSION = '2026-08-02';
  const DEFAULT_CURRENCY = 'AUD';
  let cartCache = null;
  let pendingRemoval = null;
  let drawerIntentAt = 0;
  let fullCartMeasured = false;
  let mutationFrame = null;

  const analytics = () => window.WeathercraftPhase2Analytics;

  const emit = (eventName, parameters = {}) => {
    const helper = analytics();
    if (!helper || typeof helper.emit !== 'function') return false;
    helper.emit(eventName, {
      ...parameters,
      measurement_source: 'weathercraft_measurement_repairs',
      measurement_version: VERSION,
    });
    return true;
  };

  const money = (cents) => Number(cents || 0) / 100;

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to read cart');
    cartCache = await response.json();
    return cartCache;
  };

  const cartItemToGa = (item, index = 0, quantityOverride) => {
    const quantity = Number(quantityOverride ?? item.quantity ?? 1);
    const priceCents = item.final_price ?? item.price ?? item.original_price ?? 0;
    return {
      item_id: String(item.variant_id || item.id || item.key || ''),
      item_name: item.product_title || item.title || 'Wine',
      item_brand: item.vendor || 'Weathercraft',
      item_variant: item.variant_title || undefined,
      price: money(priceCents),
      quantity,
      index,
    };
  };

  const cartPayload = (cart, source) => ({
    currency: cart?.currency || DEFAULT_CURRENCY,
    value: money(cart?.total_price || 0),
    items: Array.isArray(cart?.items) ? cart.items.map((item, index) => cartItemToGa(item, index)) : [],
    cart_view_source: source,
  });

  const emitViewCart = async (source) => {
    try {
      const cart = await fetchCart();
      if (!cart.items?.length) return;
      emit('view_cart', cartPayload(cart, source));
    } catch (error) {
      console.warn('[Weathercraft measurement] view_cart skipped:', error);
    }
  };

  const measureFullCartPage = () => {
    if (fullCartMeasured || window.location.pathname !== '/cart') return;
    fullCartMeasured = true;
    emitViewCart('cart_page');
  };

  const rememberRemoval = (button) => {
    const row = button.closest('[data-key]');
    const key = row?.dataset.key;
    if (!key || !cartCache?.items?.length) return;

    const item = cartCache.items.find((candidate) => candidate.key === key);
    if (!item) return;

    pendingRemoval = {
      key,
      item: { ...item },
      previousQuantity: Number(item.quantity || 1),
      startedAt: Date.now(),
    };

    window.setTimeout(() => {
      if (pendingRemoval?.key === key && Date.now() - pendingRemoval.startedAt >= 9000) {
        pendingRemoval = null;
      }
    }, 9500);
  };

  const handleCartUpdate = (event) => {
    const updatedCart = event.detail?.resource;
    if (!updatedCart || !Array.isArray(updatedCart.items)) return;

    cartCache = updatedCart;
    if (!pendingRemoval) return;

    const remaining = updatedCart.items.find((item) => item.key === pendingRemoval.key);
    const remainingQuantity = Number(remaining?.quantity || 0);
    if (remainingQuantity >= pendingRemoval.previousQuantity) return;

    const removedQuantity = pendingRemoval.previousQuantity - remainingQuantity;
    const removedItem = cartItemToGa(pendingRemoval.item, 0, removedQuantity);
    emit('remove_from_cart', {
      currency: updatedCart.currency || DEFAULT_CURRENCY,
      value: removedItem.price * removedQuantity,
      items: [removedItem],
      cart_remove_source: window.location.pathname === '/cart' ? 'cart_page' : 'cart_drawer',
    });
    pendingRemoval = null;
  };

  const repairEmptyCartRecommendations = (root = document) => {
    if (window.location.pathname !== '/cart') return;
    if (document.querySelector('.cart-form')) return;

    root.querySelectorAll('product-card').forEach((card) => {
      const quickAdd = card.querySelector('quick-add-component');
      if (!quickAdd || quickAdd.dataset.wcRecommendationRepaired === 'true') return;

      const productLink = card.querySelector('a.product-card__link[href*="/products/"]') ||
        card.querySelector('a[href*="/products/"]');
      const href = productLink?.getAttribute('href');
      if (!href) return;

      const link = document.createElement('a');
      link.href = href;
      link.className = 'button wc-cart-recommendation-link';
      link.textContent = 'View Wine';
      link.setAttribute('data-wc-recommendation-link', 'true');
      quickAdd.dataset.wcRecommendationRepaired = 'true';
      quickAdd.replaceWith(link);
    });
  };

  const normaliseElementalCart = (root = document) => {
    if (window.location.pathname !== '/cart' || !document.body.classList.contains('template-elemental')) return;

    root.querySelectorAll('a.cart-items__media-container, a.cart-items__title').forEach((anchor) => {
      const replacement = document.createElement('span');
      replacement.className = `${anchor.className} wc-elemental-cart-static`;
      replacement.innerHTML = anchor.innerHTML;
      replacement.setAttribute('aria-label', anchor.textContent?.trim() || 'Elemental wine');
      if (anchor.getAttribute('style')) replacement.setAttribute('style', anchor.getAttribute('style'));
      anchor.replaceWith(replacement);
    });

    root.querySelectorAll('.cart-items__wrapper').forEach((wrapper) => {
      if (wrapper.querySelector('[data-elemental-edit-selection]')) return;
      const edit = document.createElement('p');
      edit.className = 'wc-elemental-cart-edit';
      edit.innerHTML = '<a href="/pages/elemental-wine#wines" data-elemental-edit-selection>Edit selection</a>';
      const form = wrapper.querySelector('.cart-form');
      if (form) wrapper.insertBefore(edit, form);
      else wrapper.appendChild(edit);
    });
  };

  const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const winesCardItem = (card, index) => {
    const button = card.querySelector('.wc-product-card__atc');
    const link = card.querySelector('a[href*="/products/"]');
    const title = button?.dataset.productTitle || card.querySelector('.wc-product-card__title')?.textContent;
    const variantId = button?.dataset.variantId;
    const handle = link?.getAttribute('href')?.split('/products/')[1]?.split(/[?#]/)[0];
    return {
      item_id: String(variantId || handle || index + 1),
      item_name: cleanText(title),
      item_brand: 'Weathercraft',
      price: money(card.dataset.price),
      index,
      item_list_id: 'weathercraft_wines',
      item_list_name: 'Weathercraft Wines',
    };
  };

  const elementalCardName = (card) => {
    const title = card.querySelector('.elemental-wine-card__title');
    if (!title) return 'Elemental wine';
    const clone = title.cloneNode(true);
    clone.querySelector('.elemental-wine-card__per-bottle')?.remove();
    return cleanText(clone.textContent);
  };

  const elementalCardItem = (card, index) => {
    const select = card.querySelector('[data-elemental-variant-select]');
    const option = select?.selectedOptions?.[0];
    const priceMatch = option?.textContent?.match(/[$£€]\s?([\d,.]+)/);
    const parsedPrice = priceMatch ? Number(priceMatch[1].replace(/,/g, '')) : undefined;
    return {
      item_id: String(option?.value || card.dataset.productId || index + 1),
      item_name: elementalCardName(card),
      item_brand: 'Elemental by Weathercraft',
      item_variant: cleanText(option?.textContent?.split('—')[0]),
      price: Number.isFinite(parsedPrice) ? parsedPrice : undefined,
      index,
      item_list_id: 'elemental_wines',
      item_list_name: 'Elemental by Weathercraft',
    };
  };

  const measureProductLists = (root = document) => {
    root.querySelectorAll('[data-wc-catalogue]').forEach((catalogue) => {
      if (catalogue.dataset.wcListMeasured === 'true') return;
      const cards = Array.from(catalogue.querySelectorAll('.wc-product-card'));
      if (!cards.length) return;
      catalogue.dataset.wcListMeasured = 'true';
      emit('view_item_list', {
        item_list_id: 'weathercraft_wines',
        item_list_name: 'Weathercraft Wines',
        items: cards.map(winesCardItem),
      });
    });

    root.querySelectorAll('[data-elemental-landing]').forEach((landing) => {
      if (landing.dataset.wcListMeasured === 'true') return;
      const cards = Array.from(landing.querySelectorAll('[data-elemental-product-card]'));
      if (!cards.length) return;
      landing.dataset.wcListMeasured = 'true';
      emit('view_item_list', {
        item_list_id: 'elemental_wines',
        item_list_name: 'Elemental by Weathercraft',
        items: cards.map(elementalCardItem),
      });
    });
  };

  const handleProductSelection = (event) => {
    const link = event.target.closest('[data-wc-catalogue] .wc-product-card a[href*="/products/"]');
    if (!link) return;
    const card = link.closest('.wc-product-card');
    const catalogue = link.closest('[data-wc-catalogue]');
    if (!card || !catalogue) return;
    const cards = Array.from(catalogue.querySelectorAll('.wc-product-card'));
    const index = Math.max(0, cards.indexOf(card));
    emit('select_item', {
      item_list_id: 'weathercraft_wines',
      item_list_name: 'Weathercraft Wines',
      items: [winesCardItem(card, index)],
    });
  };

  const handleElementalPackSelection = (event) => {
    const select = event.target.closest('[data-elemental-variant-select]');
    if (!select) return;
    const card = select.closest('[data-elemental-product-card]');
    const option = select.selectedOptions?.[0];
    if (!card || !option) return;

    emit('wc_elemental_pack_select', {
      item_id: String(option.value),
      product_id: String(card.dataset.productId || ''),
      item_name: elementalCardName(card),
      item_variant: cleanText(option.textContent?.split('—')[0]),
      pack_label: cleanText(option.textContent),
      page_location: window.location.href,
    });
  };

  const handleCruxClick = (event) => {
    const link = event.target.closest('#club a[href^="mailto:"]');
    if (!link) return;
    emit('crux_membership_click', {
      link_text: cleanText(link.textContent),
      link_url: link.href.split('?')[0],
      page_location: window.location.href,
      membership_type: 'crux',
    });
  };

  const applyRepairs = (root = document) => {
    repairEmptyCartRecommendations(root);
    normaliseElementalCart(root);
    measureProductLists(root);
  };

  document.addEventListener('pointerdown', (event) => {
    const removeButton = event.target.closest('.cart-items__remove');
    if (removeButton) rememberRemoval(removeButton);
  }, true);

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-testid="cart-drawer-trigger"]')) drawerIntentAt = Date.now();
    handleProductSelection(event);
    handleCruxClick(event);
  }, true);

  document.addEventListener('change', handleElementalPackSelection, true);
  document.addEventListener('cart:update', handleCartUpdate, true);

  document.addEventListener('dialog:open', (event) => {
    if (!event.target.matches?.('cart-drawer-component')) return;
    if (Date.now() - drawerIntentAt > 2500) return;
    drawerIntentAt = 0;
    emitViewCart('cart_drawer');
  }, true);

  document.addEventListener('DOMContentLoaded', () => {
    fetchCart().catch(() => null);
    measureFullCartPage();
    applyRepairs();

    const observer = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => mutation.addedNodes.length || mutation.removedNodes.length)) return;
      if (mutationFrame) cancelAnimationFrame(mutationFrame);
      mutationFrame = requestAnimationFrame(() => {
        mutationFrame = null;
        applyRepairs();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
